import classNames from 'classnames';
import {
  area,
  bisect,
  bisector,
  curveBasis,
  format as d3Format,
  extent,
  group,
  line,
  scaleLinear,
  scaleOrdinal,
  scaleUtc,
} from 'd3';
import { isDate, isNumber } from 'lodash';
import { useId, useRef, useState } from 'react';

import { ReplotChartNames } from '../../constants';
import { type CommonPlotProps, FormatType } from '../../types';
import {
  getFormatter,
  isDateValid,
  useChartTheme,
  useRenderCheck,
  useResizeObserver,
} from '../../utilities';
import { getPrimaryColor } from '../../utilities/plot-colors';
import { plotTestId } from '../../utilities/plotTestId';
import { tickAnchor } from '../../utilities/tick-anchor';
import { KdeTooltip, type KdeTooltipProps } from '../kde/kde-tooltip';
import { PlotLoadWrapper } from '../plot-loader/plot-loader';
import {
  PlotTooltip,
  TooltipHoverLine,
  useTooltipController,
} from '../plot-tooltip';
import styles from './area-chart.module.css';
import { AreaTooltip, type AreaTooltipProps } from './area-tooltip';

export type AreaDatum = {
  x: number | string;
  value: number | null;
  secondaryValue?: number | null;
};

export type AreaData = {
  label: string;
  values: AreaDatum[];
  percentiles?: { p25: number; p50: number; p75: number };
};

export type NormalisedDatum = Omit<AreaDatum, 'x'> & {
  x: Date | number;
};
export type NormalisedData = Omit<AreaData, 'values'> & {
  values: NormalisedDatum[];
};

type AreaChartProps = {
  data: AreaData[];
  colors?: string[];
  showXAxis?: boolean;
  showYAxis?: boolean;
  showGradients?: boolean;
  showQuartiles?: boolean;
} & CommonPlotProps;

type QuartileOverlay = {
  seriesIndex: number;
  color: string;
  x25: number;
  x50: number;
  x75: number;
};

const Y_AXIS_WIDTH = 32;
const X_AXIS_HEIGHT = 12;

const bisectDate = bisector<Date, Date>((d) => d).center;
const isDateBasedData = (data: AreaData[]): boolean => {
  return (
    data.length > 0 &&
    // @ts-ignore noUncheckedIndexedAccess
    data[0].values.length > 0 &&
    // @ts-ignore noUncheckedIndexedAccess
    typeof data[0].values[0].x === 'string'
  );
};

const normalizeData = (
  data: AreaData[],
  isDateAxis: boolean
): NormalisedData[] => {
  return data.map((d) => {
    const values = (d.values ?? []).map((v) => {
      const x = isDateAxis ? new Date(v.x) : v.x;
      return { ...v, x } as NormalisedDatum;
    });

    const filteredValues = isDateAxis
      ? values.filter((v: NormalisedDatum) => isDateValid(v.x as Date))
      : values.filter(
          // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
          (v: NormalisedDatum) => typeof v.x === 'number' && !isNaN(v.x)
        );

    return { ...d, values: filteredValues };
  });
};

const createXScale = (
  normalisedData: NormalisedData[],
  isDateAxis: boolean,
  plotWidth: number
) => {
  const allXValues = normalisedData.flatMap((d) => d.values.map((v) => v.x));
  const xDomain = extent(allXValues);

  if (xDomain[0] == null || xDomain[1] == null) return null;

  if (isDateAxis && isDate(xDomain[0]) && isDate(xDomain[1])) {
    return scaleUtc().domain([xDomain[0], xDomain[1]]).range([0, plotWidth]);
  }
  if (!isDateAxis && isNumber(xDomain[0]) && isNumber(xDomain[1])) {
    return scaleLinear().domain([xDomain[0], xDomain[1]]).range([0, plotWidth]);
  }
  return null;
};

const createYScale = (
  normalisedData: NormalisedData[],
  plotHeight: number,
  baselineZero: boolean
) => {
  const allValues = normalisedData
    .flatMap((d) => d.values.map((v) => v.value))
    .filter((v) => v !== null);
  const yDomain = extent(allValues);

  if (
    yDomain[0] == null ||
    yDomain[1] == null ||
    !isNumber(yDomain[0]) ||
    !isNumber(yDomain[1])
  ) {
    return null;
  }

  const min = baselineZero ? 0 : yDomain[0];
  const max = yDomain[1];
  return scaleLinear().domain([min, max]).range([plotHeight, 0]);
};

const generateXTicks = (
  xScale: ReturnType<typeof scaleUtc> | ReturnType<typeof scaleLinear> | null,
  isDateAxis: boolean,
  formatValue: (value: number) => string
) => {
  if (!xScale) return undefined;

  if (isDateAxis) {
    const dateScale = xScale as ReturnType<typeof scaleUtc>;
    return dateScale.ticks(5).map((tick) => ({
      value: tick,
      label: dateScale.tickFormat()(tick),
    }));
  }
  const numericScale = xScale as ReturnType<typeof scaleLinear>;
  return numericScale
    .ticks(5)
    .map((tick: number) => ({ value: tick, label: formatValue(tick) }));
};

const generateYTicks = (
  yScale: ReturnType<typeof scaleLinear> | null,
  format: FormatType,
  formatValue: (value: number) => string
) => {
  return yScale
    ?.ticks(3)
    .filter((tick: number) =>
      format === FormatType.INTEGER ? Number.isInteger(tick) : true
    )
    .map((tick: number) => ({ value: tick, label: formatValue(tick) }));
};

const getTooltipFormatType = (format: FormatType): FormatType => {
  if (format === FormatType.SI) return FormatType.INTEGER;
  if (format === FormatType.CURRENCY) return FormatType.CURRENCY_INTEGER;
  return format;
};

export const AreaChart = ({
  data,
  colors: colorsProp,
  format = FormatType.SI,
  showXAxis = true,
  showYAxis = true,
  showGradients = false,
  showQuartiles = false,
  loading = false,
  showNoData = true,
  renderUpdate,
  currencyCode,
}: AreaChartProps) => {
  const clipId = useId();
  const isDateAxis = isDateBasedData(data);

  const paddingTop = showXAxis ? 0 : 8;
  const paddingBottom = showXAxis ? 4 : 0;

  const { containerRef, width, height } = useResizeObserver();

  const { theme, palette, isNewTheme } = useChartTheme();
  const colors = colorsProp ?? palette;
  const isDarkTheme = theme === 'dark';
  // In the new theme, gradients are always enabled by design; the
  // showGradients prop is only honoured on the legacy theme path.
  const useGradients = isNewTheme || showGradients;
  const gradientColors = [...new Set(colors)];
  const fadeGradientStart = isDarkTheme
    ? 'rgba(26, 32, 44, .9)'
    : 'rgba(255, 255, 255, .9)';
  const fadeGradientEnd = isDarkTheme
    ? 'rgba(26, 32, 44, 0)'
    : 'rgba(255, 255, 255, 0)';

  const [tooltipRows, setTooltipRows] = useState<AreaTooltipProps | null>(null);
  const [kdeTooltipValue, setKdeTooltipValue] =
    useState<KdeTooltipProps | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const chartRef = useRef<SVGGElement>(null);
  useRenderCheck(chartRef, { renderUpdate });

  const tooltipHandlerRef = useRef<
    ((position: [number, number]) => void) | null
  >(null);
  const tooltipController = useTooltipController({
    onHover: (position: [number, number]) => {
      if (tooltipHandlerRef.current) {
        tooltipHandlerRef.current(position);
      }
    },
  });

  if (!data || data.length === 0) {
    return (
      <div className={styles.container} data-testid="area-chart">
        <PlotLoadWrapper loading={loading} noData={showNoData}>
          <div>No data available</div>
        </PlotLoadWrapper>
      </div>
    );
  }

  const yAxisWidth = showYAxis ? Y_AXIS_WIDTH : 0;
  const xAxisHeight = showXAxis ? X_AXIS_HEIGHT : 0;

  const plotWidth = width - yAxisWidth;
  const plotHeight = height - xAxisHeight - paddingTop - paddingBottom;
  const isPlotSizeValid = plotWidth > 0 && plotHeight > 0;

  const normalisedData = normalizeData(data, isDateAxis);
  const xScale = createXScale(normalisedData, isDateAxis, plotWidth);
  const yScale = createYScale(normalisedData, plotHeight, !isDateAxis);

  const formatValue = getFormatter(format, { currencyCode });

  const xTicks = generateXTicks(xScale, isDateAxis, formatValue);
  const yTicks = generateYTicks(yScale, format, formatValue);

  const colorScale = scaleOrdinal<string>()
    .domain(normalisedData.map((d) => d.label))
    .range(normalisedData.map((_, i) => colors[i] || getPrimaryColor()));

  const lineGenerator =
    xScale && yScale
      ? line<NormalisedDatum>()
          .x((d) => xScale(d.x))
          .y((d) => yScale(d.value || 0))
          .defined((d) => d.value !== null)
          .curve(curveBasis)
      : null;

  const areaGenerator =
    xScale && yScale
      ? area<NormalisedDatum>()
          .x((d) => xScale(d.x))
          // @ts-ignore noUncheckedIndexedAccess
          .y0(yScale.range()[0])
          .y1((d) => yScale(d.value || 0))
          .defined((d) => d.value !== null)
          .curve(curveBasis)
      : null;

  const lines = normalisedData
    .map((d) => {
      if (!lineGenerator) return null;
      const path = lineGenerator(d.values);
      if (!path) return null;
      return { ...d, path };
    })
    .filter((d): d is NonNullable<typeof d> => d !== null)
    .map((d) => ({ ...d, color: colorScale(d.label) || getPrimaryColor() }));

  const areas = normalisedData
    .map((d) => {
      if (!areaGenerator) return null;
      const path = areaGenerator(d.values);
      if (!path) return null;
      return { ...d, path };
    })
    .filter((d): d is NonNullable<typeof d> => d !== null)
    .map((d) => ({ ...d, color: colorScale(d.label) || getPrimaryColor() }));

  const quartileOverlays: QuartileOverlay[] =
    showQuartiles && !isDateAxis && xScale
      ? normalisedData
          .map((d, i) => {
            const p = d.percentiles;
            if (!p) return null;
            const color = colorScale(d.label) || getPrimaryColor();
            return {
              seriesIndex: i,
              color,
              x25: (xScale as ReturnType<typeof scaleLinear>)(p.p25),
              x50: (xScale as ReturnType<typeof scaleLinear>)(p.p50),
              x75: (xScale as ReturnType<typeof scaleLinear>)(p.p75),
            };
          })
          .filter((d): d is QuartileOverlay => d !== null)
      : [];

  /** ================================
   * Tooltip
   ================================ */
  const tooltipFormatType = getTooltipFormatType(format);
  const tooltipFormatValue = getFormatter(tooltipFormatType, { currencyCode });

  const groupedDateData = isDateAxis
    ? group(
        normalisedData.flatMap((d) =>
          d.values.map((v) => ({ ...v, label: d.label }))
        ),
        (d) => d.x as Date
      )
    : undefined;
  const allDates =
    isDateAxis && groupedDateData ? Array.from(groupedDateData.keys()) : [];

  const handleTooltipMove = (position: [number, number]) => {
    if (!xScale) return;

    const rawX = xScale.invert(position[0]);

    // Standard tooltip
    if (isDateAxis && groupedDateData) {
      const date = rawX as Date;
      const index = bisectDate(allDates, date);
      const bisectDateValue = allDates[index];
      // @ts-ignore noUncheckedIndexedAccess
      const groupedRow = groupedDateData.get(bisectDateValue);
      if (!groupedRow || groupedRow.length === 0) return;

      setTooltipRows({
        title: `${bisectDateValue?.toLocaleString('default', {
          month: 'long',
        })} ${bisectDateValue?.getFullYear()}`,
        rows: groupedRow.map((d, i: number) => ({
          ...(groupedRow.length > 1
            ? { color: colors[i] || getPrimaryColor() }
            : { color: '' }),
          label: d.label,
          value:
            typeof d.value === 'number'
              ? `${tooltipFormatValue(d.value)}${typeof d.secondaryValue === 'number' ? ` / ${d3Format(',.0f')(d.secondaryValue)}` : ''}`
              : '-',
        })),
      });
      setTooltipPosition({ x: position[0], y: plotHeight / 2 });
    } else {
      // Kde tooltip
      if (
        typeof rawX !== 'number' ||
        normalisedData.length === 0 ||
        // @ts-ignore noUncheckedIndexedAccess
        normalisedData[0].values.length === 0
      ) {
        return;
      }

      // @ts-ignore noUncheckedIndexedAccess
      const indexArray = normalisedData[0].values.map((v) => v.x as number);
      const dataIndex = bisect(indexArray, rawX);
      const clampedIndex = Math.min(dataIndex, indexArray.length - 1);
      const datasetXValue = indexArray[clampedIndex];
      // @ts-ignore noUncheckedIndexedAccess
      const formattedValue = tooltipFormatValue(datasetXValue);

      const values = normalisedData.map((d) => {
        const safeIndex = Math.min(clampedIndex, d.values.length - 1);
        const point = d.values[safeIndex];
        const formattedCdf = d3Format('.1%')(point?.secondaryValue ?? 0);

        return {
          label: d.label,
          color: colorScale(d.label) || getPrimaryColor(),
          description: `${formattedCdf} of people make up to ${formattedValue}`,
        };
      });

      setKdeTooltipValue({ title: formattedValue, values });
      setTooltipPosition({ x: position[0], y: plotHeight / 2 });
      return;
    }
  };

  tooltipHandlerRef.current = handleTooltipMove;

  return (
    <div
      ref={containerRef}
      className={styles.container}
      data-testid={plotTestId(ReplotChartNames.AreaChart)}
    >
      <PlotLoadWrapper
        loading={loading}
        noData={showNoData && (data?.length ?? 0) === 0}
      >
        <svg width="100%" height="100%">
          <defs>
            {useGradients &&
              gradientColors.map((gradientColor) => (
                <linearGradient
                  key={gradientColor}
                  id={`area-gradient-${gradientColor}`}
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  {isNewTheme ? (
                    <>
                      <stop
                        offset="0%"
                        stopColor={gradientColor}
                        stopOpacity="0.4"
                      />
                      <stop
                        offset="81.25%"
                        stopColor={gradientColor}
                        stopOpacity="0"
                      />
                    </>
                  ) : (
                    <>
                      <stop
                        offset="0%"
                        stopColor={gradientColor}
                        stopOpacity="0.6"
                      />
                      <stop
                        offset="100%"
                        stopColor={gradientColor}
                        stopOpacity="0.1"
                      />
                    </>
                  )}
                </linearGradient>
              ))}
            {showGradients && (
              <linearGradient
                id="line-left-fade-gradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="0%"
              >
                <stop offset="0%" stopColor={fadeGradientStart} />
                <stop offset="100%" stopColor={fadeGradientEnd} />
              </linearGradient>
            )}
          </defs>
          <g transform={`translate(0, ${paddingTop})`}>
            {showYAxis && (
              <g id="y-axis" data-testid="y-axis" className={styles.yAxis}>
                {isPlotSizeValid &&
                  yTicks?.map(
                    (tick: { value: number; label: string }, i: number) => (
                      <text
                        key={i}
                        x={yAxisWidth - 4}
                        y={yScale ? yScale(tick.value) : 0}
                        alignmentBaseline="middle"
                        textAnchor="end"
                      >
                        {tick.label}
                      </text>
                    )
                  )}
              </g>
            )}
            {showXAxis && (
              <g
                id="x-axis"
                data-testid="x-axis"
                transform={`translate(${yAxisWidth}, ${plotHeight})`}
                className={styles.xAxis}
              >
                {isPlotSizeValid &&
                  xTicks?.map(
                    (
                      tick: { value: Date | number; label: string },
                      i: number
                    ) => {
                      const xPos = xScale ? xScale(tick.value) : 0;
                      return (
                        <text
                          key={i}
                          x={xPos}
                          y={X_AXIS_HEIGHT + paddingBottom}
                          style={{
                            textAnchor: tickAnchor(
                              i,
                              xTicks.length,
                              xPos,
                              plotWidth
                            ),
                          }}
                        >
                          {tick.label}
                        </text>
                      );
                    }
                  )}
              </g>
            )}
            <g
              id="chart"
              ref={chartRef}
              transform={`translate(${yAxisWidth}, 0)`}
              {...tooltipController.gProps}
              className={styles.chart}
            >
              {showQuartiles && isPlotSizeValid && (
                <defs>
                  {quartileOverlays.map((d, i) => {
                    const area = areas[d.seriesIndex];
                    return area ? (
                      <clipPath
                        key={i}
                        id={`${clipId}-area-clip-${i}`}
                        clipPathUnits="userSpaceOnUse"
                      >
                        <path d={area.path} />
                      </clipPath>
                    ) : null;
                  })}
                </defs>
              )}
              {showQuartiles &&
                isPlotSizeValid &&
                quartileOverlays.map((d, i) => (
                  <g
                    key={`quartile-overlay-${i}`}
                    clipPath={`url(#${clipId}-area-clip-${i})`}
                  >
                    <rect
                      x={d.x25}
                      y={0}
                      width={Math.max(0, d.x75 - d.x25)}
                      height={plotHeight}
                      fill={d.color}
                      className={styles.iqrBand}
                    />
                    <line
                      x1={d.x25}
                      y1={0}
                      x2={d.x25}
                      y2={plotHeight}
                      stroke={d.color}
                      className={styles.quartileLine}
                    >
                      <title>Q1 (25th percentile)</title>
                    </line>
                    <line
                      x1={d.x75}
                      y1={0}
                      x2={d.x75}
                      y2={plotHeight}
                      stroke={d.color}
                      className={styles.quartileLine}
                    >
                      <title>Q3 (75th percentile)</title>
                    </line>
                    <line
                      x1={d.x50}
                      y1={0}
                      x2={d.x50}
                      y2={plotHeight}
                      stroke={d.color}
                      className={styles.medianLine}
                    >
                      <title>Median (50th percentile)</title>
                    </line>
                  </g>
                ))}
              {lines.map((line, i) => (
                <path
                  key={i}
                  d={line.path}
                  stroke={line.color}
                  className={classNames(styles.line, 'area-line-stroke')}
                />
              ))}
              {areas.map((area, i) => (
                <path
                  key={i}
                  d={area.path}
                  className={styles.area}
                  fill={
                    useGradients
                      ? `url(#area-gradient-${area.color})`
                      : area.color
                  }
                  fillOpacity={useGradients ? undefined : 0.2}
                  data-testid="area-chart__area"
                />
              ))}
              {isPlotSizeValid && (
                <rect
                  x={0}
                  y={0}
                  width={plotWidth}
                  height={plotHeight}
                  fill="transparent"
                />
              )}
              {isPlotSizeValid && showGradients && (
                <rect
                  x={-1}
                  width={plotWidth + 1}
                  height={plotHeight + 1}
                  fill="url(#line-left-fade-gradient)"
                />
              )}
              {tooltipController.isVisible && (
                <TooltipHoverLine x={tooltipPosition.x} height={plotHeight} />
              )}
            </g>
          </g>
        </svg>
        <PlotTooltip
          isVisible={tooltipController.isVisible}
          x={tooltipPosition.x}
          y={tooltipPosition.y}
        >
          {isDateAxis
            ? tooltipRows && (
                <AreaTooltip
                  title={tooltipRows.title}
                  rows={tooltipRows.rows}
                />
              )
            : kdeTooltipValue && (
                <KdeTooltip
                  title={kdeTooltipValue.title}
                  values={kdeTooltipValue.values}
                />
              )}
        </PlotTooltip>
      </PlotLoadWrapper>
    </div>
  );
};
